<template>
    <DsFormField class="ds-dropdown" :class="wrapperClass" v-bind="wrapperProps">
        <template #default="{ class: inputClasses }">
            <Select
                :name="id"
                :disabled
                :invalid="!isValid"
                :options
                optionLabel="label"
                optionValue="value"
                optionDisabled="disabled"
                :scroll-height
                :pt="getDropdownPtOptions(inputClasses, dataTest, dataTestid)"
                v-model="modelValue"
                v-bind="attrs"
                :virtual-scroller-options="virtualScrollerOptions"
            >
                <template #dropdownicon="dropdownIconProps">
                    <DsButton
                        type="text"
                        size="sm"
                        :class="[dropdownIconProps.class, 'ds-dropdown__select-icon-button']"
                        icon-left="chevron-down"
                        aria-hidden="true"
                        tabindex="-1"
                        :is-in-storybook
                    ></DsButton>
                </template>
                <template #value="slotValueProps">
                    <div class="ds-dropdown__item ds-dropdown__item--value">
                        <template v-if="currentSelectedOption">
                            <DsIcon
                                v-if="currentSelectedOption.icon"
                                :name="currentSelectedOption.icon"
                                class="ds-dropdown__item-icon"
                                :is-in-storybook
                            />
                            <span class="ds-dropdown__item-text">
                                {{ currentSelectedOption.label }}
                            </span>
                        </template>
                        <span class="ds-dropdown__placeholder" v-else>
                            {{ slotValueProps.placeholder }}
                        </span>
                    </div>
                </template>

                <template #option="slotOptionProps">
                    <div
                        class="ds-dropdown__item ds-dropdown__item--option"
                        :disabled="slotOptionProps.option.disabled"
                    >
                        <DsIcon
                            v-if="slotOptionProps.option.icon"
                            :name="slotOptionProps.option.icon"
                            class="ds-dropdown__item-icon"
                            :is-in-storybook
                        />
                        <span class="ds-dropdown__item-text">
                            {{ slotOptionProps.option.label }}
                        </span>
                    </div>
                </template>
            </Select>
        </template>
        <template #inputRight></template>
    </DsFormField>
</template>

<script setup lang="ts">
import { computed, ComputedRef, useAttrs } from "vue";
import Select from "primevue/select";
import DsIcon from "../../icon/ds-icon.vue";
import DsButton from "../../button/ds-button.vue";
import DsFormField, { DsFormFieldProps, stripFormFieldProps } from "./general/ds-form-field.vue";
import { DsFormFieldValueType } from "../models/ds-form-field-models";
import { useBreakpoints } from "@sazka/utils/composables/use-breakpoints";

import { getDropdownPtOptions } from "./ds-dropdown/ds-dropdown-pt";
import { DsDropdownItemModel } from "./ds-dropdown/ds-dropdown-models";

defineOptions({ inheritAttrs: false });
const { class: wrapperClass, ...attrs } = useAttrs();

const virtualScrollerLimit = 50;

/**
 * Selected value of the dropdown.
 */
const modelValue = defineModel<DsFormFieldValueType>();

const props = defineProps<
    DsFormFieldProps & {
        /**
         * Array of options for the dropdown. Each option should have a label, value and others.
         */
        options: DsDropdownItemModel[];

        /**
         * Data test attribute for testing purposes
         */
        dataTest?: string;
        /**
         * Fallback label for the dropdown. If the value is not in the options (or is nullish), this label will be used.
         */
        fallbackLabel?: string;

        dataTestid?: string;
    }
>();
const wrapperProps = computed(() => stripFormFieldProps(props));

const isLg = useBreakpoints("lg");
const itemSize = computed(() => (!isLg.value ? 40 : 48));
const virtualScrollerOptions = computed(() =>
    props.options.length > virtualScrollerLimit ? { itemSize: itemSize.value } : undefined
);

const scrollHeight = computed(() => `${itemSize.value * 5}px`);

// slotValueProps doesn't have valid information about currentOption
const currentSelectedOption: ComputedRef<DsDropdownItemModel> = computed(() => {
    if (modelValue.value || modelValue.value == 0) {
        return props?.options.find((option) => option.value == modelValue.value);
    }

    if (props.fallbackLabel) {
        return {
            label: props.fallbackLabel,
            value: null,
        };
    }

    return null;
});

const isValid = computed(() => !props.errorMessage);
</script>
